import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ChevronRight, Shield, Users, MapPin, Handshake } from 'lucide-react';

const SERDAR_PHOTO = "/serdar-dogan-foto.jpg";

const values = [
  {
    icon: Shield,
    title: 'Diskretion',
    description: 'Ihre Privatsphäre hat für mich höchste Priorität. Alle Transaktionen werden mit absoluter Vertraulichkeit behandelt.'
  },
  {
    icon: MapPin,
    title: 'Marktkenntnis',
    description: 'Als Spezialist für das Rhein-Main-Gebiet kenne ich jeden Stadtteil, jede Lage und alle relevanten Marktentwicklungen.'
  },
  {
    icon: Users,
    title: 'Persönlichkeit',
    description: 'Bei mir haben Sie einen festen Ansprechpartner. Keine wechselnden Sachbearbeiter, sondern persönliche Betreuung von A bis Z.'
  },
  {
    icon: Handshake,
    title: 'Langfristige Beziehungen',
    description: 'Meine Kunden kommen wieder – oder empfehlen mich weiter. Das ist der beste Beweis für Qualität und Vertrauen.'
  }
];

const regions = [
  'Frankfurt am Main',
  'Wiesbaden',
  'Darmstadt',
  'Offenbach am Main',
  'Mainz',
  'Bad Homburg',
  'Hanau',
  'Königstein',
  'Oberursel',
  'Kronberg'
];

export default function UeberUns() {
  return (
    <div data-testid="ueber-uns-page">
      {/* Hero with Photo */}
      <section className="relative pt-20 bg-[#0a1120]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
            {/* Photo - Full width on mobile, half on desktop */}
            <div className="relative h-[50vh] lg:h-[80vh] lg:-mr-8">
              <img
                src={SERDAR_PHOTO}
                alt="Serdar Dogan - Immobilienexperte"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a1120] via-transparent to-transparent lg:bg-gradient-to-r lg:from-transparent lg:via-transparent lg:to-[#0a1120]" />
            </div>

            {/* Content */}
            <div className="relative z-10 py-12 lg:py-24 lg:pl-8 flex items-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                {/* Breadcrumb */}
                <nav className="flex items-center gap-2 text-sm mb-8">
                  <Link to="/" className="text-[#c8c6c4] hover:text-[#d1af6a]">Home</Link>
                  <ChevronRight className="w-4 h-4 text-[#c8c6c4]" />
                  <span className="text-[#d1af6a]">Über uns</span>
                </nav>

                <span className="font-accent text-[#d1af6a] text-xs tracking-[0.2em]">
                  ÜBER MICH
                </span>
                <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white mt-4 mb-6">
                  Serdar Dogan
                </h1>
                <p className="text-[#d1af6a] text-lg mb-6">
                  Immobilienexperte im Rhein-Main-Gebiet
                </p>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Biography */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="prose prose-lg prose-invert max-w-none"
          >
            <div className="border-l-2 border-[#d1af6a] pl-8 space-y-6">
              <p className="text-[#c8c6c4] text-lg leading-relaxed">
                Mein Name ist Serdar Dogan, und seit vielen Jahren bin ich im Immobiliengeschäft des Rhein-Main-Gebiets tätig. 
                Als studierter Betriebswirt (B.A.) verbinde ich wirtschaftliches Fachwissen mit einem tiefen Verständnis für den regionalen Immobilienmarkt. 
                Was mich antreibt, ist die Überzeugung, dass Immobilien mehr sind als nur Quadratmeter – sie sind Lebensräume, 
                Investitionen und oft auch emotionale Entscheidungen.
              </p>
              <p className="text-[#c8c6c4] text-lg leading-relaxed">
                In einer Branche, die oft von Schnelligkeit und Anonymität geprägt ist, 
                setze ich auf das Gegenteil: persönliche Beratung, fundiertes Fachwissen 
                und absolute Diskretion. Meine Kunden schätzen, dass sie bei mir einen 
                festen Ansprechpartner haben, der sich Zeit nimmt und ihre Interessen 
                konsequent vertritt.
              </p>
              <p className="text-[#c8c6c4] text-lg leading-relaxed">
                Ob Sie verkaufen, kaufen oder vermieten möchten – ich begleite Sie mit 
                Engagement und Expertise. Denn am Ende zählt nicht die Transaktion, 
                sondern das Vertrauen, das bleibt.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-[#1a2640]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="font-accent text-[#d1af6a] text-xs tracking-[0.2em]">
              MEINE WERTE
            </span>
            <h2 className="font-display text-3xl sm:text-4xl text-white mt-4">
              Was mich auszeichnet
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="flex gap-6"
              >
                <div className="w-14 h-14 bg-[#d1af6a]/10 flex items-center justify-center flex-shrink-0">
                  <value.icon className="w-7 h-7 text-[#d1af6a]" />
                </div>
                <div>
                  <h3 className="font-subheading text-xl text-white mb-2">
                    {value.title}
                  </h3>
                  <p className="text-[#c8c6c4] leading-relaxed">
                    {value.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Regions */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="font-accent text-[#d1af6a] text-xs tracking-[0.2em]">
                REGIONALE EXPERTISE
              </span>
              <h2 className="font-display text-3xl sm:text-4xl text-white mt-4 mb-6">
                Das Rhein-Main-Gebiet ist mein Zuhause
              </h2>
              <p className="text-[#c8c6c4] leading-relaxed mb-8">
                Als gebürtiger Frankfurter kenne ich das Rhein-Main-Gebiet wie meine 
                Westentasche. Von den exklusiven Lagen in Bad Homburg und Königstein 
                bis zu den aufstrebenden Vierteln in Offenbach – ich weiß, wo der 
                Markt steht und wohin er sich entwickelt.
              </p>
              <p className="text-[#c8c6c4] leading-relaxed">
                Dieses lokale Wissen ist ein entscheidender Vorteil für meine Kunden. 
                Denn nur wer den Markt wirklich kennt, kann den besten Preis erzielen 
                oder das perfekte Objekt finden.
              </p>
            </div>
            <div className="card-luxury p-8">
              <h3 className="font-subheading text-xl text-white mb-6">
                Meine Fokus-Regionen
              </h3>
              <div className="grid grid-cols-2 gap-4">
                {regions.map((region) => (
                  <div key={region} className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-[#d1af6a]" />
                    <span className="text-[#c8c6c4] text-sm">{region}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#0a1120] border-t border-[#d1af6a]/20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl sm:text-3xl text-white mb-4">
            Lassen Sie uns ins Gespräch kommen
          </h2>
          <p className="text-[#c8c6c4] mb-8">
            Ich freue mich darauf, Sie kennenzulernen und Ihr Immobilienanliegen zu besprechen.
          </p>
          <Link to="/kontakt" className="btn-primary">
            Kontakt aufnehmen
          </Link>
        </div>
      </section>
    </div>
  );
}
